import { ReactNode } from "react";
import Nav from "react-bootstrap/Nav";
import BSTab from "react-bootstrap/Tab";
import { TabContainerProps } from "react-bootstrap/TabContainer";
import { NavLink } from "react-router-dom";
import useResponsive from "../../../hooks/useResponsive";
import styles from "./Tabs.module.css";
import { v4 as uuidv4 } from "uuid";

export type Tab = {
  id?: string;
  disabled?: boolean;
  title?: ReactNode;
  link?: string;
  customTitle?: ReactNode;
  titleClassName?: string;
  body?: ReactNode;
  bodyClassName?: string;
};
type TabsProps = {
  tabs: Tab[];
  navBarClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  onTabHover?: (tabId: string) => void;
  onTabHoverEnd?: () => void;
} & TabContainerProps;

const Tabs = ({
  tabs,
  defaultActiveKey,
  navBarClassName,
  contentClassName,
  transition = false,
  disabled,
  onTabHover,
  onTabHoverEnd,
  ...rest
}: TabsProps) => {
  const tabsNav: ReactNode[] = [];
  const tabsContent: ReactNode[] = [];

  const { isMobile } = useResponsive();

  const handleTabHover = (tabId: string) => {
    if (onTabHover) onTabHover(tabId);
  };

  const handleTabHoverEnd = () => {
    if (onTabHoverEnd) onTabHoverEnd();
  };

  const createTabs = () => {
    for (let i = 0; i < tabs.length; i++) {
      const {
        id,
        disabled: tabDisabled,
        title = "",
        customTitle,
        titleClassName,
        body,
        bodyClassName,
        link,
      } = tabs[i];

      const navItem = (
        <Nav.Item key={uuidv4()} id={id} className={titleClassName}>
          {!customTitle ? (
            <Nav.Link
              key={uuidv4()}
              as="div"
              eventKey={id}
              disabled={disabled || tabDisabled}
              className={`font-md ${link ? "p-0" : isMobile ? "p-2" : ""}`}
              onMouseOver={() => handleTabHover(id!)}
              onMouseOut={handleTabHoverEnd}
            >
              {link ? (
                <NavLink
                  to={link}
                  className={({ isActive }) =>
                    `${styles.link} text-decoration-none ${
                      isActive ? "active" : ""
                    }`
                  }
                >
                  {title}
                </NavLink>
              ) : (
                title
              )}
            </Nav.Link>
          ) : (
            customTitle
          )}
        </Nav.Item>
      );

      tabsNav.push(navItem);

      if (body) {
        tabsContent.push(
          <BSTab.Pane key={id} id={id} eventKey={id} className={bodyClassName}>
            {body}
          </BSTab.Pane>
        );
      }
    }
  };

  createTabs();

  return (
    <div className="app-tabs h-100 d-flex flex-column">
      <BSTab.Container
        transition={transition}
        defaultActiveKey={defaultActiveKey}
        {...rest}
      >
        <Nav
          variant="tabs"
          className={` ${styles.tabsNav} ${navBarClassName} border-bottom-0`}
        >
          {tabsNav}
        </Nav>
        <BSTab.Content className={contentClassName}>
          {tabsContent}
        </BSTab.Content>
      </BSTab.Container>
    </div>
  );
};

export default Tabs;
